import React, { useState } from 'react';
import { EventFormData, AgendaData, LineupData, SponsorData, SponsorTypeData } from './types';
import AgendaModal from './Components/AgendaModal';
import LineupModal from './Components/LineupModal';
import SponsorModal from './Components/SponsorModal';
import AgendaList from './Components/AgendaList';
import LineupList from './Components/LineupList';
import SponsorList from './Components/SponsorList';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import { getAllSponsorTypes } from '../../../api';
import { Calendar, Music, Handshake, Plus } from 'lucide-react';

interface EventOtherDetailsTabProps {
  formData: EventFormData;
  onInputChange: (field: keyof EventFormData, value: any) => void;
  activeTab: number;
}

const EventOtherDetailsTab: React.FC<EventOtherDetailsTabProps> = ({
  formData,
  onInputChange,
  activeTab
}) => {
  const [isAgendaModalOpen, setIsAgendaModalOpen] = useState(false);
  const [isLineupModalOpen, setIsLineupModalOpen] = useState(false);
  const [isSponsorModalOpen, setIsSponsorModalOpen] = useState(false);
  const [editingAgenda, setEditingAgenda] = useState<AgendaData | undefined>(undefined);
  const [editingLineup, setEditingLineup] = useState<LineupData | undefined>(undefined);
  const [editingSponsor, setEditingSponsor] = useState<SponsorData | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [sponsorTypes, setSponsorTypes] = React.useState<SponsorTypeData[]>([]);

  const [confirmationDialog, setConfirmationDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'warning' as 'warning' | 'danger' | 'info' | 'success'
  });

  React.useEffect(() => {
    (async () => {
      try {
        const res = await getAllSponsorTypes();
        setSponsorTypes(res);
      } catch (e) {
        console.error('Error loading sponsor types:', e);
      }
    })();
  }, []);

  const showConfirmationDialog = (title: string, message: string, onConfirm: () => void, type: 'warning' | 'danger' | 'info' | 'success' = 'warning') => {
    setConfirmationDialog({
      isOpen: true,
      title,
      message,
      onConfirm,
      type
    });
  };

  const closeConfirmationDialog = () => {
    setConfirmationDialog(prev => ({ ...prev, isOpen: false }));
  };

  const handleAddAgenda = () => {
    setEditingAgenda(undefined);
    setIsAgendaModalOpen(true);
  };

  const handleEditAgenda = (agenda: AgendaData) => {
    setEditingAgenda(agenda);
    setIsAgendaModalOpen(true);
  };

  const handleSaveAgenda = (agenda: AgendaData) => {
    setIsLoading(true);
    try {
      let updatedAgenda = [...formData.agenda];
      if (editingAgenda) {
        const index = updatedAgenda.findIndex(a => a.id === editingAgenda.id);
        if (index !== -1) {
          updatedAgenda[index] = { ...agenda, id: editingAgenda.id };
        }
      } else {
        const newAgenda = {
          ...agenda,
          id: `agenda_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          sortOrder: updatedAgenda.length
        };
        updatedAgenda.push(newAgenda);
      }
      onInputChange('agenda', updatedAgenda);
      setIsAgendaModalOpen(false);
      setEditingAgenda(undefined);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAgenda = (agendaId: string) => {
    const agendaToDelete = formData.agenda.find(a => a.id === agendaId);
    if (agendaToDelete) {
      showConfirmationDialog(
        'Delete Agenda Item',
        `Are you sure you want to delete agenda item "${agendaToDelete.title}"?`,
        () => {
          const updatedAgenda = formData.agenda.filter(a => a.id !== agendaId);
          onInputChange('agenda', updatedAgenda);
        },
        'danger'
      );
    }
  };

  const handleToggleAgenda = (agendaId: string, isActive: boolean) => {
    const updatedAgenda = formData.agenda.map(a => 
      a.id === agendaId ? { ...a, isActive } : a
    );
    onInputChange('agenda', updatedAgenda);
  };

  const handleAddLineup = () => {
    setEditingLineup(undefined);
    setIsLineupModalOpen(true);
  };

  const handleEditLineup = (lineup: LineupData) => {
    setEditingLineup(lineup);
    setIsLineupModalOpen(true);
  };

  const handleSaveLineup = (lineup: LineupData) => {
    setIsLoading(true);
    try {
      let updatedLineup = [...formData.lineup];
      if (editingLineup) {
        const index = updatedLineup.findIndex(l => l.id === editingLineup.id);
        if (index !== -1) {
          updatedLineup[index] = { ...lineup, id: editingLineup.id };
        }
      } else {
        const newLineup = {
          ...lineup,
          id: `lineup_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          sortOrder: updatedLineup.length
        };
        updatedLineup.push(newLineup);
      }
      onInputChange('lineup', updatedLineup);
      setIsLineupModalOpen(false);
      setEditingLineup(undefined);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteLineup = (lineupId: string) => {
    const lineupToDelete = formData.lineup.find(l => l.id === lineupId);
    if (lineupToDelete) {
      showConfirmationDialog(
        'Delete Lineup Item',
        `Are you sure you want to delete lineup item "${lineupToDelete.name}"?`,
        () => {
          const updatedLineup = formData.lineup.filter(l => l.id !== lineupId);
          onInputChange('lineup', updatedLineup);
        },
        'danger'
      );
    }
  };

  const handleToggleLineup = (lineupId: string, isActive: boolean) => {
    const updatedLineup = formData.lineup.map(l => 
      l.id === lineupId ? { ...l, isActive } : l
    );
    onInputChange('lineup', updatedLineup);
  };

  const handleAddSponsor = () => {
    setEditingSponsor(undefined);
    setIsSponsorModalOpen(true);
  };

  const handleEditSponsor = (sponsor: SponsorData) => {
    setEditingSponsor(sponsor);
    setIsSponsorModalOpen(true);
  };

  const handleSaveSponsor = (sponsor: SponsorData) => {
    setIsLoading(true);
    try {
      let updatedSponsors = [...formData.sponsors];
      if (editingSponsor) {
        const index = updatedSponsors.findIndex(s => s.id === editingSponsor.id);
        if (index !== -1) {
          updatedSponsors[index] = { ...sponsor, id: editingSponsor.id };
        }
      } else {
        const newSponsor = {
          ...sponsor,
          id: `sponsor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        };
        updatedSponsors.push(newSponsor);
      }
      onInputChange('sponsors', updatedSponsors);
      setIsSponsorModalOpen(false);
      setEditingSponsor(undefined);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSponsor = (sponsorId: string) => {
    const sponsorToDelete = formData.sponsors.find(s => s.id === sponsorId);
    if (sponsorToDelete) {
      showConfirmationDialog(
        'Delete Sponsor',
        `Are you sure you want to delete sponsor "${sponsorToDelete.name}"?`,
        () => {
          const updatedSponsors = formData.sponsors.filter(s => s.id !== sponsorId);
          onInputChange('sponsors', updatedSponsors);
        },
        'danger'
      );
    }
  };

  const handleToggleSponsor = (sponsorId: string, isActive: boolean) => {
    const updatedSponsors = formData.sponsors.map(s => 
      s.id === sponsorId ? { ...s, isActive } : s
    );
    onInputChange('sponsors', updatedSponsors);
  };

  return (
    <div className={`step-tab-panel ${activeTab === 4 ? 'active' : ''}`} style={{ display: activeTab === 4 ? "flex" : "none", flexDirection: "column", gap: 24 }}>
      
      {/* Card 1: Event Agenda */}
      <div className="glass-panel" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div className="v3-section-head" style={{ margin: 0 }}>
            <Calendar size={14} color="#EE3F7C" /> Event Agenda ({formData.agenda.length})
          </div>
          <button type="button" className="v3-btn-secondary" style={{ fontSize: 12 }} onClick={handleAddAgenda}>
            <Plus size={14} /> Add Agenda Item
          </button>
        </div>
        <AgendaList
          agenda={formData.agenda}
          onEditAgenda={handleEditAgenda}
          onDeleteAgenda={handleDeleteAgenda}
          onToggleAgenda={handleToggleAgenda}
        />
      </div>

      {/* Card 2: Event Lineup */}
      <div className="glass-panel" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div className="v3-section-head" style={{ margin: 0 }}>
            <Music size={14} color="#EE3F7C" /> Event Lineup ({formData.lineup.length})
          </div>
          <button type="button" className="v3-btn-secondary" style={{ fontSize: 12 }} onClick={handleAddLineup}>
            <Plus size={14} /> Add Lineup Item
          </button>
        </div>
        <LineupList
          lineup={formData.lineup}
          onEditLineup={handleEditLineup}
          onDeleteLineup={handleDeleteLineup}
          onToggleLineup={handleToggleLineup}
        />
      </div>

      {/* Card 3: Event Sponsors */}
      <div className="glass-panel" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div className="v3-section-head" style={{ margin: 0 }}>
            <Handshake size={14} color="#EE3F7C" /> Event Sponsors ({formData.sponsors.length})
          </div>
          <button type="button" className="v3-btn-secondary" style={{ fontSize: 12 }} onClick={handleAddSponsor}>
            <Plus size={14} /> Add Sponsor
          </button>
        </div>
        <SponsorList
          sponsors={formData.sponsors}
          onEditSponsor={handleEditSponsor}
          onDeleteSponsor={handleDeleteSponsor}
          onToggleSponsor={handleToggleSponsor}
          sponsorTypes={sponsorTypes}
        />
      </div>

      {/* Modals */}
      <AgendaModal
        isOpen={isAgendaModalOpen}
        toggle={() => setIsAgendaModalOpen(false)}
        onSubmit={handleSaveAgenda}
        isLoading={isLoading}
        agenda={editingAgenda}
        formData={formData}
      />
      <LineupModal
        isOpen={isLineupModalOpen}
        toggle={() => setIsLineupModalOpen(false)}
        onSubmit={handleSaveLineup}
        isLoading={isLoading}
        lineup={editingLineup}
      />
      <SponsorModal
        isOpen={isSponsorModalOpen}
        toggle={() => setIsSponsorModalOpen(false)}
        onSubmit={handleSaveSponsor}
        isLoading={isLoading}
        sponsor={editingSponsor}
        sponsorTypes={sponsorTypes}
      />
      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        toggle={closeConfirmationDialog}
        onConfirm={confirmationDialog.onConfirm}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        type={confirmationDialog.type}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        icon="fa-solid fa-exclamation-triangle"
      />
    </div>
  );
};

export default EventOtherDetailsTab;
